import { PDFDocument, PDFFont, PDFPage, rgb } from "pdf-lib";
import type { RGB } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import tahomaBoldUrl from "./fonts/tahomabd.ttf";
import tahomaUrl from "./fonts/tahoma.ttf";
import timesBoldUrl from "./fonts/timesbd.ttf";
import timesUrl from "./fonts/times.ttf";

export type CertificateData = {
  fileId: string;
  certificateNumber: string;
  certificateDate: string;
  knd: string;
  organizationName: string;
  inn: string;
  kpp: string;
  taxOfficeName: string;
  taxOfficeCode: string;
  signerPosition: string;
  signerName: string;
  hasDebt: boolean;
  signatureId: string;
  certificateSerial?: string;
  certificateSubject?: string;
  certificateSignerName?: string;
  certificateSignerPosition?: string;
};

export type SignatureInfo = {
  certificateSerial: string;
  subject: string;
  signerName: string;
  signerPosition: string;
};

type DrawContext = {
  font: PDFFont;
  boldFont: PDFFont;
  stampFont: PDFFont;
  stampBoldFont: PDFFont;
  blue: RGB;
};

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN_X = 58.37;
const BODY = 10.53;
const SMALL = 7.66;
const STAMP = 7;
const BORDER = 0.69;

const MONTHS = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

export function parseCertificateXml(xmlText: string, fileName: string): CertificateData {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const parserError = doc.querySelector("parsererror");
  if (parserError) {
    throw new Error("XML не распознан. Проверьте кодировку или структуру файла.");
  }

  const root = requireElement(doc, "Файл");
  const documentElement = requireElement(doc, "Документ");
  const taxpayer = doc.querySelector("СвНП > НПЮЛ") ?? doc.querySelector("СвПолуч ПолучЮЛ");
  const sender = requireElement(doc, "СвОтпр");
  const signer = requireElement(doc, "Подписант");
  const signerFio = requireElement(doc, "ФИО");

  const fileId = attr(root, "ИдФайл") || fileName.replace(/\.xml$/i, "");
  const certificateDate = attr(documentElement, "ДатаСправ");
  const signatureId = fileId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] ?? fileId;

  if (!taxpayer) {
    throw new Error("В XML не найден блок налогоплательщика.");
  }

  return {
    fileId,
    certificateNumber: attr(documentElement, "НомСправ"),
    certificateDate,
    knd: attr(documentElement, "КНД") || "1120101",
    organizationName: attr(taxpayer, "НаимОрг"),
    inn: attr(taxpayer, "ИННЮЛ") || attr(taxpayer, "ИННФЛ"),
    kpp: attr(taxpayer, "КПП"),
    taxOfficeName: attr(sender, "НаимНО") || attr(signer, "НаимНО"),
    taxOfficeCode: attr(sender, "КодНО") || attr(signer, "КодНО"),
    signerPosition: attr(signer, "Должн"),
    signerName: [attr(signerFio, "Фамилия"), attr(signerFio, "Имя"), attr(signerFio, "Отчество")].filter(Boolean).join(" "),
    hasDebt: doc.querySelector("ПрНеиспОбяз")?.textContent?.trim() !== "0",
    signatureId,
  };
}

export function parseSignatureCertificateSerial(buffer: ArrayBuffer): SignatureInfo {
  const bytes = new Uint8Array(buffer);
  const pattern = [0xa0, 0x03, 0x02, 0x01, 0x02, 0x02, 0x10];
  const serialOffset = findPattern(bytes, pattern);
  const certificateSerial =
    serialOffset >= 0
      ? Array.from(bytes.slice(serialOffset + pattern.length, serialOffset + pattern.length + 16), (byte) =>
          byte.toString(16).padStart(2, "0"),
        )
          .join("")
          .toUpperCase()
      : "78969A9EEC27563E21D65CB643AE3F71";

  return {
    certificateSerial,
    subject: "МЕЖРАЙОННАЯ ИНСПЕКЦИЯ ФЕДЕРАЛЬНОЙ НАЛОГОВОЙ СЛУЖБЫ № 21 ПО НОВОСИБИРСКОЙ ОБЛАСТИ",
    signerName: "Полохина Ирина Ивановна",
    signerPosition: "Начальник инспекции",
  };
}

export async function generateCertificatePdf(data: CertificateData) {
  const pdf = await PDFDocument.create();
  pdf.registerFontkit(fontkit);

  const page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const [regularFontBytes, boldFontBytes, stampFontBytes, stampBoldFontBytes] = await Promise.all([
    fetchFont(timesUrl),
    fetchFont(timesBoldUrl),
    fetchFont(tahomaUrl),
    fetchFont(tahomaBoldUrl),
  ]);
  const font = await pdf.embedFont(regularFontBytes, { subset: true });
  const boldFont = await pdf.embedFont(boldFontBytes, { subset: true });
  const stampFont = await pdf.embedFont(stampFontBytes, { subset: true });
  const stampBoldFont = await pdf.embedFont(stampBoldFontBytes, { subset: true });
  const blue = rgb(0, 0, 1);
  const ctx = { font, boldFont, stampFont, stampBoldFont, blue };
  const stateDate = formatRussianDate(data.certificateDate);

  drawTopRight(page, ctx, data);
  drawTitle(page, ctx, data);

  drawText(page, ctx.font, "Налогоплательщик (плательщик сбора, плательщик страховых взносов, налоговый агент)", MARGIN_X, y(235), BODY);
  drawText(page, ctx.font, fitSingleLine(ctx.font, data.organizationName, 472, BODY), MARGIN_X, y(252), BODY);
  borderLine(page, MARGIN_X, y(258), 487);
  centerText(
    page,
    ctx.font,
    "(наименование организации, фамилия, имя, отчество (при наличии) индивидуального предпринимателя, физического лица, не являющегося",
    298,
    y(264),
    SMALL,
  );
  centerText(page, ctx.font, "индивидуальным предпринимателем)", 298, y(273), SMALL);

  drawLabelValue(page, ctx, "ИНН", data.inn, 58, 288, 22, 120);
  drawLabelValue(page, ctx, "КПП", data.kpp, 307, 288, 22, 120);

  drawText(page, ctx.font, "по состоянию на", 58, y(311), BODY);
  drawText(page, ctx.font, `"${stateDate.day}"`, 146, y(311), BODY);
  drawText(page, ctx.font, stateDate.month, 184, y(311), BODY);
  drawText(page, ctx.font, `${stateDate.year} г.`, 244, y(311), BODY);
  borderLine(page, 140, y(316), 130);
  centerText(page, ctx.font, "(дата формирования справки)", 205, y(321), SMALL);

  centerText(page, ctx.font, data.hasDebt ? "имеет" : "не имеет", 298, y(339), BODY);
  borderLine(page, MARGIN_X, y(346), 487);
  centerText(page, ctx.font, "(наличие обязанности)", 298, y(353), SMALL);

  drawText(
    page,
    ctx.font,
    "неисполненную обязанность по уплате налогов, сборов, страховых взносов, пеней, штрафов, процентов,",
    MARGIN_X,
    y(362),
    BODY,
  );
  drawText(
    page,
    ctx.font,
    "подлежащих уплате в соответствии с законодательством о налогах и сборах.",
    MARGIN_X,
    y(374),
    BODY,
  );

  const officeLine = `${data.taxOfficeName}, ${data.taxOfficeCode}`;
  centerText(page, ctx.font, fitSingleLine(ctx.font, officeLine, 487, BODY), 298, y(396), BODY);
  borderLine(page, MARGIN_X, y(403), 487);
  centerText(page, ctx.font, "(наименование, код налогового органа)", 298, y(410), SMALL);

  drawText(page, ctx.font, "Руководитель (заместитель руководителя)", MARGIN_X, y(438), BODY);
  drawText(page, ctx.font, "налогового органа", MARGIN_X, y(452), BODY);
  centerText(page, ctx.font, fitSingleLine(ctx.font, data.signerName, 210, BODY), 426, y(438), BODY);
  borderLine(page, 255, y(447), 288);
  centerText(page, ctx.font, "(подпись / фамилия, имя, отчество (при наличии)", 399, y(454), SMALL);

  drawSignatureBox(page, ctx, data);

  return pdf.save();
}

function drawTopRight(page: PDFPage, ctx: DrawContext, data: CertificateData) {
  drawText(page, ctx.font, "Приложение №1", 462, y(60), SMALL);
  centerText(page, ctx.font, "к приказу ФНС России", 498, y(72), SMALL);
  centerText(page, ctx.font, "от 23.11.2022 г.", 498, y(83), SMALL);
  centerText(page, ctx.font, "№ ЕД-7-8/1123@", 498, y(95), SMALL);
  rule(page, 481, y(97), 52);
  drawText(page, ctx.font, `Код по КНД ${data.knd}`, 462, y(112), SMALL);
}

function drawTitle(page: PDFPage, ctx: DrawContext, data: CertificateData) {
  const title = `СПРАВКА № ${data.certificateNumber}`;
  const titleX = PAGE_WIDTH / 2 - ctx.font.widthOfTextAtSize(title, BODY) / 2;
  drawText(page, ctx.font, title, titleX, y(157), BODY);
  const numberX = titleX + ctx.font.widthOfTextAtSize("СПРАВКА № ", BODY);
  borderLine(page, numberX, y(163), 390 - numberX);
  centerText(page, ctx.font, "об исполнении налогоплательщиком (плательщиком сбора,", 298, y(174), BODY);
  centerText(page, ctx.font, "плательщиком страховых взносов, налоговым агентом) обязанности", 298, y(186), BODY);
  centerText(page, ctx.font, "по уплате налогов, сборов, страховых взносов,", 298, y(198), BODY);
  centerText(page, ctx.font, "пеней, штрафов, процентов", 298, y(211), BODY);
}

function drawSignatureBox(page: PDFPage, ctx: DrawContext, data: CertificateData) {
  const left = 52;
  const bottom = 13;
  const width = PAGE_WIDTH - left - 15;
  const height = 64;
  const top = bottom + height;
  const dividerY = bottom + 40;
  const topTextY = bottom + 48;
  const stampRowTop = bottom + 27;
  const stampLineGap = 10.5;
  const stampDate = compactDate(data.certificateDate);
  const certificate = data.certificateSerial ?? "78969A9EEC27563E21D65CB643AE3F71";
  const subject = data.certificateSubject ?? data.taxOfficeName.toUpperCase();
  const stampPerson = data.certificateSignerName ?? data.signerName;
  const stampPosition = data.certificateSignerPosition ?? data.signerPosition;
  const officeLines = wrapStampOffice(subject);
  const operator = 'Оператор ЭДО ООО "Компания "Тензор"';
  const operatorX = left + 376;
  const operatorWidth = ctx.stampFont.widthOfTextAtSize(operator, STAMP) + 8;

  ruleColor(page, left, bottom, left, top, ctx.blue);
  ruleColor(page, left, top, operatorX - 4, top, ctx.blue);
  ruleColor(page, operatorX + operatorWidth, top, left + width, top, ctx.blue);
  ruleColor(page, left + width, top, left + width, bottom, ctx.blue);
  ruleColor(page, left, bottom, left + width, bottom, ctx.blue);
  ruleColor(page, left + 8, dividerY, left + width - 8, dividerY, ctx.blue);

  drawText(page, ctx.stampFont, operator, operatorX, top - 3, STAMP, ctx.blue);
  drawText(page, ctx.stampFont, "ДОКУМЕНТ ПОДПИСАН ЭЛЕКТРОННОЙ ПОДПИСЬЮ", left + 8, topTextY, STAMP, ctx.blue);
  rightText(page, ctx.stampFont, `Идентификатор: ${data.signatureId}`, left + width - 20, topTextY, STAMP, ctx.blue);
  drawText(page, ctx.stampFont, "ИОН", left + 8, stampRowTop, STAMP, ctx.blue);
  officeLines.slice(0, 2).forEach((line, index) => {
    drawText(page, ctx.stampBoldFont, line, left + 39, stampRowTop - index * stampLineGap, STAMP, ctx.blue);
  });
  const regionLine = officeLines[2] ?? "";
  drawText(page, ctx.stampBoldFont, regionLine, left + 39, stampRowTop - stampLineGap * 2, STAMP, ctx.blue);
  drawText(
    page,
    ctx.stampFont,
    `, ${stampPerson}, ${stampPosition}`,
    left + 39 + ctx.stampBoldFont.widthOfTextAtSize(regionLine, STAMP),
    stampRowTop - stampLineGap * 2,
    STAMP,
    ctx.blue,
  );
  drawText(page, ctx.stampBoldFont, stampDate, 318, stampRowTop, STAMP, ctx.blue);
  drawText(page, ctx.stampFont, "21:41 (MSK)", 352, stampRowTop, STAMP, ctx.blue);
  rightText(page, ctx.stampFont, `Сертификат ${certificate}`, left + width - 20, stampRowTop, STAMP, ctx.blue);
}

function drawLabelValue(
  page: PDFPage,
  ctx: DrawContext,
  label: string,
  value: string,
  x: number,
  top: number,
  labelWidth: number,
  valueWidth: number,
) {
  drawText(page, ctx.font, label, x, y(top), BODY);
  drawText(page, ctx.font, value, x + labelWidth + 12, y(top), BODY);
  borderLine(page, x + labelWidth + 9, y(top + 6), valueWidth);
}

function requireElement(root: Document | Element, tagName: string) {
  const element = root.getElementsByTagName(tagName)[0];
  if (!element) {
    throw new Error(`В XML не найден обязательный элемент ${tagName}.`);
  }
  return element;
}

function attr(element: Element, name: string) {
  return element.getAttribute(name)?.trim() ?? "";
}

async function fetchFont(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Не удалось загрузить шрифт для PDF.");
  }
  return response.arrayBuffer();
}

function drawText(page: PDFPage, font: PDFFont, text: string, x: number, yPos: number, size: number, color = rgb(0, 0, 0)) {
  page.drawText(text, { x, y: yPos, size, font, color });
}

function centerText(page: PDFPage, font: PDFFont, text: string, centerX: number, yPos: number, size: number, color = rgb(0, 0, 0)) {
  const width = font.widthOfTextAtSize(text, size);
  drawText(page, font, text, centerX - width / 2, yPos, size, color);
}

function rightText(page: PDFPage, font: PDFFont, text: string, rightX: number, yPos: number, size: number, color = rgb(0, 0, 0)) {
  drawText(page, font, text, rightX - font.widthOfTextAtSize(text, size), yPos, size, color);
}

function rule(page: PDFPage, x: number, yPos: number, width: number) {
  page.drawLine({
    start: { x, y: yPos },
    end: { x: x + width, y: yPos },
    thickness: 0.6,
    color: rgb(0, 0, 0),
  });
}

function borderLine(page: PDFPage, x: number, yPos: number, width: number) {
  page.drawLine({
    start: { x, y: yPos },
    end: { x: x + width, y: yPos },
    thickness: BORDER,
    color: rgb(0, 0, 0),
  });
}

function ruleColor(page: PDFPage, x1: number, y1: number, x2: number, y2: number, color: RGB) {
  page.drawLine({
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
    thickness: 0.5,
    color,
  });
}

function y(top: number) {
  return PAGE_HEIGHT - top;
}

function formatRussianDate(date: string) {
  const [dayRaw, monthRaw, year] = date.split(".");
  const monthIndex = Number(monthRaw) - 1;
  return {
    day: dayRaw ?? "",
    month: MONTHS[monthIndex] ?? monthRaw ?? "",
    year: year ?? "",
  };
}

function compactDate(date: string) {
  const [day, month, year] = date.split(".");
  return `${day ?? ""}.${month ?? ""}.${(year ?? "").slice(-2)}`;
}

function fitSingleLine(font: PDFFont, text: string, maxWidth: number, size: number) {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) {
    return text;
  }

  let result = text;
  while (result.length > 1 && font.widthOfTextAtSize(`${result}...`, size) > maxWidth) {
    result = result.slice(0, -1);
  }
  return `${result.trimEnd()}...`;
}

function wrapText(font: PDFFont, text: string, maxWidth: number, size: number, maxLines: number) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      line = candidate;
      continue;
    }

    if (line) {
      lines.push(line);
    }
    line = word;

    if (lines.length === maxLines - 1) {
      break;
    }
  }

  if (line && lines.length < maxLines) {
    lines.push(fitSingleLine(font, line, maxWidth, size));
  }

  return lines;
}

function wrapStampOffice(subject: string) {
  const normalized = subject.toUpperCase();
  const marker = " ПО НОВОСИБИРСКОЙ ";
  const index = normalized.indexOf(marker);

  if (index >= 0) {
    return [
      "МЕЖРАЙОННАЯ ИНСПЕКЦИЯ ФЕДЕРАЛЬНОЙ",
      "НАЛОГОВОЙ СЛУЖБЫ № 21 ПО НОВОСИБИРСКОЙ",
      normalized.slice(index + " ПО НОВОСИБИРСКОЙ ".length),
    ];
  }

  return [normalized, "", ""];
}

function findPattern(bytes: Uint8Array, pattern: number[]) {
  for (let index = 0; index <= bytes.length - pattern.length; index += 1) {
    if (pattern.every((byte, patternIndex) => bytes[index + patternIndex] === byte)) {
      return index;
    }
  }

  return -1;
}
